import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useNotify } from '@/hooks/useNotify';
import { useAuthStore } from '@/stores/authStore';
import {
  useAutomationRecipes,
  useAutomationRuleEvents,
  useAutomationRuleRuns,
  useAutomationRunDetail,
  useStartAutomationRun,
  useTransitionAutomationRule,
  type AutomationRule,
} from '@/hooks/useAutomation';

function catalogKey(key: string): string {
  return key.replace(/^automation\./, '');
}

const STATUS_COLOR: Record<string, 'default' | 'info' | 'success' | 'warning'> = {
  draft: 'default',
  dry_run: 'info',
  active: 'success',
  paused: 'warning',
  disabled: 'default',
};

interface RuleDetailDialogProps {
  rule: AutomationRule | null;
  onClose: () => void;
  killSwitch: boolean;
}

export function RuleDetailDialog({ rule, onClose, killSwitch }: RuleDetailDialogProps) {
  const { t } = useTranslation('automation');
  const { t: tc } = useTranslation('common');
  const { i18n } = useTranslation();

  /**
   * Translate a value that came from the server, falling back to the raw
   * token (#2720).
   *
   * i18next echoes the key back when it is missing, so comparing against it
   * keeps an unlabelled status readable instead of printing the key path.
   */
  const translateOr = (prefix: string, value: string): string => {
    const key = `${prefix}.${value}`;
    const translated = t(key);
    return translated === key ? value : translated;
  };
  const translateRunStatus = (value: string) => translateOr('runStatus', value);
  const translateRuleStatus = (value: string) => translateOr('status', value);

  /** Finnish date order; `toLocaleString()` with no locale gave 9/29/2026. */
  const formatTimestamp = (value: string) =>
    new Date(value).toLocaleString(i18n.language);
  const notify = useNotify();
  const canManage = useAuthStore((s) => s.hasPermission('automation.rules.manage'));
  const recipes = useAutomationRecipes(Boolean(rule));
  const events = useAutomationRuleEvents(rule?.id ?? null);
  const runs = useAutomationRuleRuns(rule?.id ?? null);
  const transition = useTransitionAutomationRule();
  const startRun = useStartAutomationRun();
  const [previewRunId, setPreviewRunId] = useState<string | null>(null);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const preview = useAutomationRunDetail(previewRunId);

  const recipe = recipes.data?.find((item) => item.id === rule?.recipeId);

  const act = async (action: Parameters<typeof transition.mutateAsync>[0]['action']) => {
    if (!rule) return;
    try {
      await transition.mutateAsync({ id: rule.id, action, expectedVersion: rule.version });
    } catch {
      notify.error(t('transitionFailed'));
    }
  };

  const run = async (mode: 'dry_run' | 'live') => {
    if (!rule) return;
    try {
      const result = await startRun.mutateAsync({
        id: rule.id,
        mode,
        input: { targetEntities: [] },
      });
      setPreviewRunId(result.runId);
      notify.success(mode === 'dry_run' ? t('previewStarted') : t('runStarted'));
    } catch {
      notify.error(t('transitionFailed'));
    }
  };

  if (!rule) return null;

  return (
    <>
      <ResponsiveDialog open onClose={onClose} maxWidth="md" fullWidth mobileTitle={rule.name}>
        <DialogTitle>{rule.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" color={STATUS_COLOR[rule.status]} label={t(`status.${rule.status}`)} />
              {recipe && <Chip size="small" label={t(`risk.${recipe.riskClass}`, recipe.riskClass)} />}
              {rule.needsReapproval && <Chip size="small" color="warning" label={t('needsReapproval')} />}
            </Stack>
            {recipe && (
              <Typography variant="body2" color="text.secondary">
                {t(catalogKey(recipe.descriptionKey))}
              </Typography>
            )}
            {rule.description && <Typography variant="body2">{rule.description}</Typography>}
            {killSwitch && <Alert severity="error">{t('killSwitchBlocks')}</Alert>}
            {recipe && (
              <Box>
                <Typography variant="subtitle2">{t('triggers')}</Typography>
                {recipe.triggers.map((trigger) => (
                  <Typography key={`${trigger.kind}-${trigger.insightType ?? ''}`} variant="body2">
                    {trigger.kind === 'insight'
                      ? t('triggerInsight', { type: trigger.insightType ?? '' })
                      : t('triggerManual')}
                  </Typography>
                ))}
                <Typography variant="subtitle2" sx={{ mt: 1 }}>{t('commands')}</Typography>
                {recipe.allowedCommands.map((cmd) => (
                  <Typography key={cmd.name} variant="body2">
                    {t(catalogKey(cmd.descriptionKey), cmd.name)}
                  </Typography>
                ))}
                {rule.limits.maxRunsPerDay != null && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {t('maxRunsPerDay', { count: rule.limits.maxRunsPerDay })}
                  </Typography>
                )}
              </Box>
            )}

            {canManage && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {rule.status === 'draft' && (
                  <Button size="small" onClick={() => void act('dry-run-mode')}>{t('dryRunMode')}</Button>
                )}
                {(rule.status === 'dry_run' || rule.status === 'draft') && (
                  <Button size="small" variant="contained" onClick={() => void act('activate')}>{t('activate')}</Button>
                )}
                {rule.status === 'active' && (
                  <Button size="small" onClick={() => void act('pause')}>{t('pause')}</Button>
                )}
                {rule.status === 'paused' && (
                  <Button size="small" onClick={() => void act('resume')}>{t('resume')}</Button>
                )}
                {rule.needsReapproval && rule.status !== 'disabled' && (
                  <Button size="small" onClick={() => void act('reapprove')}>{t('reapprove')}</Button>
                )}
                {rule.status !== 'disabled' && (
                  <Button size="small" color="error" onClick={() => setConfirmDisable(true)}>{t('disable')}</Button>
                )}
                {recipe?.dryRunSupported && (
                  <Button size="small" variant="outlined" onClick={() => void run('dry_run')} disabled={killSwitch}>
                    {t('preview')}
                  </Button>
                )}
              </Stack>
            )}

            {preview.data && (
              <Box>
                <Typography variant="subtitle2">{t('steps')}</Typography>
                {preview.data.steps.map((step) => (
                  <Typography key={step.id} variant="body2">
                    {step.commandName}: {String((step.request as { preview?: string }).preview ?? step.status)}
                  </Typography>
                ))}
              </Box>
            )}

            <Box>
              <Typography variant="subtitle2">{t('runs')}</Typography>
              {(runs.data ?? []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">{t('noRuns')}</Typography>
              ) : (
                (runs.data ?? []).map((item) => (
                  <Typography key={item.id} variant="body2">
                    {/*
                      `mode` was translated while `status` on the same line was
                      not, so the row read "Koeajo · succeeded · …" — half
                      Finnish, half English (#2720). Unknown statuses fall back
                      to the raw value rather than rendering an empty gap.
                    */}
                    {t(`runMode.${item.mode}`)} · {translateRunStatus(item.status)} ·{' '}
                    {formatTimestamp(item.createdAt)}
                  </Typography>
                ))
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2">{t('events')}</Typography>
              {(events.data ?? []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">{t('noEvents')}</Typography>
              ) : (
                (events.data ?? []).slice(0, 12).map((event) => (
                  <Typography key={event.id} variant="body2">
                    {/*
                      Event types are emitted by the executor and not enumerated
                      anywhere the frontend can see, so they stay raw. The rule
                      statuses around them are translated, which is the part a
                      user actually reads.
                    */}
                    {event.eventType}
                    {event.fromStatus && event.toStatus
                      ? ` ${translateRuleStatus(event.fromStatus)} → ${translateRuleStatus(event.toStatus)}`
                      : ''}
                    {' · '}
                    {formatTimestamp(event.createdAt)}
                  </Typography>
                ))
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{tc('close')}</Button>
        </DialogActions>
      </ResponsiveDialog>
      <ConfirmDialog
        open={confirmDisable}
        onCancel={() => setConfirmDisable(false)}
        onConfirm={() => {
          setConfirmDisable(false);
          void act('disable');
        }}
        title={t('disable')}
        message={t('disable')}
        confirmColor="error"
      />
    </>
  );
}
